import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, ThumbsUp, Loader2, Clock } from "lucide-react";

export default function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [communityApproved, setCommunityApproved] = useState(0);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/submissions`);
      setItems(res.data.items || []);
      setPendingCount(res.data.pending_count || 0);
      setCommunityApproved(res.data.community_approved || 0);
      setLastRefreshedAt(Date.now());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const approve = async (id) => {
    setActing(id);
    try {
      await axios.post(`${API}/admin/submissions/${id}/approve`);
      toast.success("Submission approved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to approve");
    } finally { setActing(null); }
  };

  const reject = async (id) => {
    setActing(id);
    try {
      await axios.post(`${API}/admin/submissions/${id}/reject`);
      toast.success("Submission rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reject");
    } finally { setActing(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="submissions-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={load} lastRefreshedAt={lastRefreshedAt} testId="submissions-refresh" /></div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-amber-50 rounded-lg p-2.5 text-center border border-amber-200">
          <p className="text-lg font-bold text-amber-600" data-testid="submissions-pending-count">{pendingCount}</p>
          <p className="text-[10px] text-amber-500">Pending</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-2.5 text-center border border-emerald-200">
          <p className="text-lg font-bold text-emerald-600">{items.filter(i => i.status === "approved").length}</p>
          <p className="text-[10px] text-emerald-500">Approved</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-2.5 text-center border border-purple-200">
          <p className="text-lg font-bold text-purple-600" data-testid="community-approved-count">{communityApproved}</p>
          <p className="text-[10px] text-purple-500">Community</p>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No submissions yet</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((sub) => (
            <div key={sub.id} className={`rounded-lg border p-3 ${sub.status === "pending" ? "bg-white border-amber-200" : sub.status === "approved" ? "bg-emerald-50/50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
                 data-testid={`submission-item-${sub.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{sub.business_name}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      sub.status === "pending" ? "bg-amber-100 text-amber-700" :
                      sub.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                      "bg-red-100 text-red-700"
                    }`}>{sub.status}</span>
                    {sub.approved_by === "community_vote" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">community</span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{sub.category} {sub.location && `· ${sub.location}`}</p>
                  {sub.offer_description && <p className="text-xs text-slate-500 mt-1 line-clamp-1">{sub.offer_description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                    <span className="flex items-center gap-0.5"><ThumbsUp className="w-3 h-3" />{sub.upvotes || 0} votes</span>
                    <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{sub.created_at?.slice(0, 10)}</span>
                    <span>by {sub.user_name}</span>
                  </div>
                </div>
                {sub.status === "pending" && (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => approve(sub.id)} disabled={acting === sub.id}
                      className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center justify-center transition"
                      data-testid={`approve-btn-${sub.id}`}>
                      {acting === sub.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => reject(sub.id)} disabled={acting === sub.id}
                      className="w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center transition"
                      data-testid={`reject-btn-${sub.id}`}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
